export const escapeSelectorAttrValue = (value: string): string => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

export const buildHighlightReactionAnchorSelectors = (
  noteId: string,
  cfiRange?: string,
): string[] => {
  const selectors = [`g[data-id="${escapeSelectorAttrValue(noteId)}"]`]

  if (cfiRange) {
    const escapedCfiRange = escapeSelectorAttrValue(cfiRange)
    selectors.push(`g[data-cfi-range="${escapedCfiRange}"]`)
    selectors.push(`g[data-epubcfi="${escapedCfiRange}"]`)
  }

  return selectors
}

export const findHighlightReactionAnchorGroup = (
  root: ParentNode,
  noteId: string,
  cfiRange?: string,
): SVGGElement | null => {
  const selectors = buildHighlightReactionAnchorSelectors(noteId, cfiRange)

  for (const selector of selectors) {
    const match = root.querySelector(selector)
    if (match instanceof SVGGElement) {
      return match
    }
  }

  return null
}

export const sortHighlightRects = (
  rects: Iterable<SVGRectElement>,
): SVGRectElement[] => {
  return Array.from(rects).sort((a, b) => {
    const aBottom =
      parseFloat(a.getAttribute('y') || '0') +
      parseFloat(a.getAttribute('height') || '0')
    const bBottom =
      parseFloat(b.getAttribute('y') || '0') +
      parseFloat(b.getAttribute('height') || '0')
    if (aBottom !== bBottom) {
      return bBottom - aBottom
    }

    const aRight =
      parseFloat(a.getAttribute('x') || '0') +
      parseFloat(a.getAttribute('width') || '0')
    const bRight =
      parseFloat(b.getAttribute('x') || '0') +
      parseFloat(b.getAttribute('width') || '0')
    return bRight - aRight
  })
}

export const getReactionBadgeMetrics = (fontSize?: number) => {
  const baseFontSize =
    typeof fontSize === 'number' && Number.isFinite(fontSize) && fontSize > 0
      ? fontSize
      : 16
  const emojiSize = Math.max(18, Math.round(baseFontSize * 0.95))
  const itemGap = Math.max(4, Math.round(emojiSize * 0.16))
  const itemWidth = emojiSize + Math.max(2, Math.round(emojiSize * 0.12))
  const pillPaddingX = Math.max(5, Math.round(emojiSize * 0.24))
  const pillHeight = Math.round(emojiSize * 1.3)
  const highlightGap = Math.max(6, Math.round(emojiSize * 0.3))

  return {
    emojiSize,
    itemGap,
    itemWidth,
    pillPaddingX,
    pillHeight,
    highlightGap,
  }
}

const APPLE_EMOJI_ASSET_BASE_URL =
  'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/'

export const emojiToUnified = (emoji?: string | null): string | null => {
  const normalizedEmoji = emoji?.trim()
  if (!normalizedEmoji) {
    return null
  }

  const unified = Array.from(normalizedEmoji)
    .map((char) => char.codePointAt(0)?.toString(16).padStart(4, '0'))
    .filter((codePoint): codePoint is string => Boolean(codePoint))
    .join('-')

  return unified || null
}

export const getAppleEmojiImageUrl = (emoji?: string | null): string | null => {
  const unified = emojiToUnified(emoji)
  return unified ? `${APPLE_EMOJI_ASSET_BASE_URL}${unified}.png` : null
}

type CalculateReactionBadgeXOptions = {
  preferredX: number
  pillWidth: number
  svgWidth?: number
  visibleLeft?: number
  visibleRight?: number
  margin?: number
}

export const calculateReactionBadgeX = ({
  preferredX,
  pillWidth,
  svgWidth = 0,
  visibleLeft,
  visibleRight,
  margin = 6,
}: CalculateReactionBadgeXOptions): number => {
  const hasVisibleBounds =
    typeof visibleLeft === 'number' &&
    Number.isFinite(visibleLeft) &&
    typeof visibleRight === 'number' &&
    Number.isFinite(visibleRight) &&
    visibleRight > visibleLeft

  const lowerBound = hasVisibleBounds ? visibleLeft + margin : margin
  const upperBound = hasVisibleBounds
    ? visibleRight - pillWidth - margin
    : svgWidth > 0
      ? svgWidth - pillWidth - margin
      : preferredX

  if (upperBound < lowerBound) {
    return lowerBound
  }

  return Math.max(lowerBound, Math.min(preferredX, upperBound))
}

type ResolveReactionBadgeClampBoundsOptions = {
  anchorLeft: number
  anchorRight: number
  visibleLeft?: number
  visibleRight?: number
}

export const resolveReactionBadgeClampBounds = ({
  anchorLeft,
  anchorRight,
  visibleLeft,
  visibleRight,
}: ResolveReactionBadgeClampBoundsOptions): {
  appliedVisibleLeft?: number
  appliedVisibleRight?: number
  anchorIntersectsVisibleBounds: boolean
} => {
  const hasVisibleBounds =
    typeof visibleLeft === 'number' &&
    Number.isFinite(visibleLeft) &&
    typeof visibleRight === 'number' &&
    Number.isFinite(visibleRight) &&
    visibleRight > visibleLeft

  if (!hasVisibleBounds) {
    return {
      anchorIntersectsVisibleBounds: false,
    }
  }

  const anchorIntersectsVisibleBounds =
    anchorRight > visibleLeft && anchorLeft < visibleRight

  if (!anchorIntersectsVisibleBounds) {
    return {
      anchorIntersectsVisibleBounds,
    }
  }

  return {
    appliedVisibleLeft: visibleLeft,
    appliedVisibleRight: visibleRight,
    anchorIntersectsVisibleBounds,
  }
}

type ResolveReactionBadgePreferredPlacementOptions = {
  fallbackX: number
  fallbackCenterY: number
  pillWidth: number
  spacerLeft?: number
  spacerWidth?: number
  followingContentLeft?: number
  followingContentLineCenterY?: number
}

const REACTION_BADGE_INLINE_FOLLOWING_TOLERANCE_PX = 1

export const resolveReactionBadgePreferredPlacement = ({
  fallbackX,
  fallbackCenterY,
  pillWidth,
  spacerLeft,
  spacerWidth,
  followingContentLeft,
  followingContentLineCenterY,
}: ResolveReactionBadgePreferredPlacementOptions): {
  preferredX: number
  lineCenterY: number
  usesFollowingContentLineCenter: boolean
  followingContentContinuesAfterSpacer: boolean
} => {
  const hasSpacerSlot =
    typeof spacerLeft === 'number' &&
    Number.isFinite(spacerLeft) &&
    typeof spacerWidth === 'number' &&
    Number.isFinite(spacerWidth) &&
    spacerWidth > 0

  const preferredX = hasSpacerSlot
    ? spacerLeft + spacerWidth - pillWidth
    : fallbackX

  const spacerRight = hasSpacerSlot ? spacerLeft + spacerWidth : undefined
  const followingContentContinuesAfterSpacer =
    hasSpacerSlot &&
    typeof spacerRight === 'number' &&
    Number.isFinite(spacerRight) &&
    typeof followingContentLeft === 'number' &&
    Number.isFinite(followingContentLeft) &&
    followingContentLeft >=
      spacerRight - REACTION_BADGE_INLINE_FOLLOWING_TOLERANCE_PX

  const usesFollowingContentLineCenter =
    followingContentContinuesAfterSpacer &&
    typeof followingContentLineCenterY === 'number' &&
    Number.isFinite(followingContentLineCenterY)

  const lineCenterY =
    usesFollowingContentLineCenter
      ? followingContentLineCenterY
      : fallbackCenterY

  return {
    preferredX,
    lineCenterY,
    usesFollowingContentLineCenter,
    followingContentContinuesAfterSpacer,
  }
}

export const getReactionSignature = (
  reactions?: Record<string, string>,
): string => {
  return Object.entries(reactions || {})
    .filter(([, emoji]) => Boolean(emoji))
    .sort(([userA, emojiA], [userB, emojiB]) => {
      const userComparison = userA.localeCompare(userB)
      if (userComparison !== 0) {
        return userComparison
      }
      return emojiA.localeCompare(emojiB)
    })
    .map(([user, emoji]) => `${user}:${emoji}`)
    .join('|')
}
